import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { tap, switchMap, catchError, finalize } from 'rxjs/operators';
import { MemoizationService } from './memoization.service';
import { LoadingService } from './loading.service';

export interface RefreshConfig {
  key: string;
  ttl?: number; // Time to live in milliseconds
  forceRefresh?: boolean;
}

export interface RefreshCallback<T = any> {
  (config?: RefreshConfig): Observable<T> | Promise<T> | T;
}

@Injectable({
  providedIn: 'root'
})
export class PullToRefreshService {
  private refreshCallbacks = new Map<string, RefreshCallback>();
  private refreshSubjects = new Map<string, Subject<void>>();
  private lastRefreshTimes = new Map<string, number>();

  constructor(
    private memoizationService: MemoizationService,
    private loadingService: LoadingService
  ) {}

  /**
   * Register a refresh callback for a specific key
   */
  registerRefreshCallback(key: string, callback: RefreshCallback): void {
    this.refreshCallbacks.set(key, callback);
    this.refreshSubjects.set(key, new Subject<void>());
  }

  /**
   * Unregister a refresh callback
   */
  unregisterRefreshCallback(key: string): void {
    this.refreshCallbacks.delete(key);
    this.refreshSubjects.delete(key);
    this.lastRefreshTimes.delete(key);
  }

  /**
   * Get observable for refresh events on a specific key
   */
  getRefreshObservable(key: string): Observable<void> {
    if (!this.refreshSubjects.has(key)) {
      this.refreshSubjects.set(key, new Subject<void>());
    }
    return this.refreshSubjects.get(key)!.asObservable();
  }

  /**
   * Trigger refresh for a specific key or all keys
   */
  async refresh(key?: string, config: RefreshConfig = { key: key || 'global' }): Promise<void> {
    const keysToRefresh = key ? [key] : Array.from(this.refreshCallbacks.keys());

    for (const refreshKey of keysToRefresh) {
      await this.refreshSingle(refreshKey, config);
    }
  }

  /**
   * Refresh a single key with caching and loading management
   */
  private async refreshSingle(key: string, config: RefreshConfig): Promise<void> {
    const callback = this.refreshCallbacks.get(key);
    if (!callback) return;

    const now = Date.now();
    const lastRefresh = this.lastRefreshTimes.get(key) || 0;
    const ttl = config.ttl || 30000; // 30 seconds default

    // Check if refresh is needed (not cached or forced)
    if (!config.forceRefresh && (now - lastRefresh) < ttl) {
      return;
    }

    this.loadingService.setLoading(true, `refresh-${key}`);

    try {
      const result = await this.executeCallback(callback, config);
      this.lastRefreshTimes.set(key, now);
      this.memoizationService.memoize(key, () => result, ttl);

      // Notify subscribers
      const subject = this.refreshSubjects.get(key);
      if (subject) {
        subject.next();
      }
    } catch (error) {
      console.error(`Error refreshing ${key}:`, error);
      throw error;
    } finally {
      this.loadingService.setLoading(false, `refresh-${key}`);
    }
  }

  /**
   * Execute the refresh callback
   */
  private async executeCallback(callback: RefreshCallback, config: RefreshConfig): Promise<any> {
    const result = callback(config);

    if (result instanceof Observable) {
      return result.toPromise();
    } else if (result instanceof Promise) {
      return result;
    } else {
      return result;
    }
  }

  /**
   * Get cached data if available
   */
  getCachedData<T>(key: string): T | null {
    try {
      return this.memoizationService.memoize(key, () => null, 0);
    } catch {
      return null;
    }
  }

  /**
   * Clear cache for specific key or all
   */
  clearCache(key?: string): void {
    if (key) {
      this.memoizationService.clearKey(key);
      this.lastRefreshTimes.delete(key);
    } else {
      this.memoizationService.clearCache();
      this.lastRefreshTimes.clear();
    }
  }

  /**
   * Check if data is stale for a key
   */
  isStale(key: string, ttl: number = 30000): boolean {
    const lastRefresh = this.lastRefreshTimes.get(key);
    if (!lastRefresh) return true;

    return (Date.now() - lastRefresh) >= ttl;
  }

  /**
   * Get time since last refresh
   */
  getTimeSinceLastRefresh(key: string): number {
    const lastRefresh = this.lastRefreshTimes.get(key);
    return lastRefresh ? Date.now() - lastRefresh : Infinity;
  }
}